import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import moment from 'moment-timezone'
import { connect } from 'react-redux'
import { AppState } from '../../../../reducers'
import { SessionState, isVendor } from '../../../../reducers/auth/session'
import { getToken } from '../../../../utils/fetchApi'
import { FiltersBar, searchToFilters } from '../../../Utils/Filters/Filter'
import { FilterFromMonth } from '../../../Utils/Filters'
import { Box, Button, CircularProgress, LinearProgress, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Tooltip, TableSortLabel } from '@mui/material'
import { FiletypeXls } from 'react-bootstrap-icons'
import { formattedCurrency } from '../../../../utils/StringUtils'
import { useTranslation } from 'react-i18next'
import { ISTableHeaderCell, ISTableRow, ISTableTitle } from '../../../Utils/ISTable'
import { useThemeContext } from '../../../../theme/ThemeContext'

type BrandId = number

type ClientRow = {
  codigo_cliente: number
  nombre?: string
  isChaco: boolean

  totalsByBrand: Record<number, number>

  total: number
  goal: number

  /**
   * Porcentaje del total comprado respecto del objetivo.
   */
  scope: number

  /**
   * Porcentaje que representa el objetivo del cliente
   * dentro del objetivo total del vendedor.
   */
  portfolioPercentage: number

  /**
   * true únicamente cuando total > goal.
   */
  reached: boolean

  deficit: number

  goalId?: number | null
  calculationRunId?: number | null
}

type VendorPayload = {
  codeZone: number
  zone: string

  codeVendor: number
  vendor: string

  brands: BrandId[]
  brandNames: Record<number, string>

  clients: ClientRow[]

  total: number
  goal: number
  scope: number
  deficit: number

  coverageBaseClients: number
  coverageReachedClients: number

  coverageScope: number
  participationScope: number

  coverageWeight: number
  participationWeight: number

  coverageContribution: number
  participationContribution: number

  goal2Scope: number

  brandClientCountCurrent?: Record<number, number>
  brandClientCountPeriod?: Record<number, number>

  totalClientsCurrent?: number
  totalClientsPeriod?: number

  sourceFrom?: string
  sourceTo?: string
  historicalMonths?: number

  count?: number
  totalVendors?: number
}

type Objective2VendorPayload =
  VendorPayload & {
    participatesObjective2: boolean

    assignmentPercentage: number

    possiblePrize: number
    reachedPrize: number

    coveragePossiblePrize: number
    coverageReachedPrize: number

    participationPossiblePrize: number
    participationReachedPrize: number

    participationReachedClients: number
  }

type PersonObjective2Payload = {
  personId: number
  personName: string

  possiblePrize: number
  reachedPrize: number
  prizeScope: number

  coverageBaseClients: number
  coverageReachedClients: number
  coverageScope: number

  participationBaseClients: number
  participationReachedClients: number
  participationClientsScope: number

  vendors: Objective2VendorPayload[]
}

interface Props {
  session: SessionState
}

type OrderableColumn =
  | 'codigo_cliente'
  | 'nombre'
  | 'portfolioPercentage'
  | 'coverage'
  | 'participation'
  | 'goal'
  | 'total'
  | 'scope'
  | 'deficit'
  | `brand-${number}`

const VendorClientBrandMatrixSSE: React.FC<Props> = ({ session }) => {
  const history = useHistory()
  const { darkModeEnabled } = useThemeContext()
  const { t } = useTranslation(['goal', 'person', 'client'])

  const [, setFilters] = useState(searchToFilters(history.location.search))

  const [isFetching, setIsFetching] = useState(false)
  const [progress, setProgress] = useState<{ count: number; total: number }>({ count: 0, total: 0 })
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [sourcePeriod, setSourcePeriod] = useState<{ sourceFrom: string | null, sourceTo: string | null, historicalMonths: number | null }>({
    sourceFrom: null, sourceTo: null, historicalMonths: null
  })

  const [data, setData] = useState<VendorPayload[]>([])
  const [people, setPeople] = useState<PersonObjective2Payload[]>([])
  const [selectedPerson, setSelectedPerson] = useState<PersonObjective2Payload | null>(null)
  const [selectedVendor, setSelectedVendor] = useState<Objective2VendorPayload | null>(null)
  const [brands, setBrands] = useState<BrandId[]>([])
  const [brandNames, setBrandNames] = useState<Record<number, string>>({})

  const eventSourceRef = useRef<EventSource | null>(null)

  const [orderBy, setOrderBy] = useState<OrderableColumn>('codigo_cliente')
  const [orderDirection, setOrderDirection] = useState<'asc' | 'desc'>('asc')

  const initDefaultFilters = {
    from: moment()
      .tz('America/Argentina/Buenos_Aires')
      .startOf('month')
      .format()
  }
  const getVendorTotalsByBrand = (v: VendorPayload) => {
    const totals: Record<number, number> = {}
    brands.forEach(b => totals[b] = 0)

    v.clients?.forEach(c => {
      brands.forEach(b => {
        totals[b] += Number(c.totalsByBrand?.[b] ?? 0)
      })
    })

    return totals
  }

  /*
    const getVendorTotal = (totalsByBrand: Record<number, number>) =>
      brands.reduce((acc, b) => acc + Number(totalsByBrand[b] ?? 0), 0)
  */

  useEffect(() => {
    setFilters(searchToFilters(history.location.search))
  }, [history.location.search])

  const startStream = () => {
    setIsFetching(true)

    setProgress({
      count: 0,
      total: 0
    })

    setDownloadUrl(null)
    setError(null)

    setSourcePeriod({
      sourceFrom: null,
      sourceTo: null,
      historicalMonths: null
    })

    setData([])

    setBrands([])
    setBrandNames({})

    setPeople([])
    setSelectedPerson(null)
    setSelectedVendor(null)

    const token = getToken()

    if (!token) {
      setError(
        'No se encontró el token de autenticación.'
      )

      setIsFetching(false)
      return
    }

    const filters =
      searchToFilters(
        history.location.search
      )

    const targetMonth =
      moment(
        filters.from ??
        initDefaultFilters.from
      )
        .tz(
          'America/Argentina/Buenos_Aires'
        )
        .format('YYYY-MM')

    const params =
      new URLSearchParams({
        targetMonth,
        access_token: token
      })

    const url =
      `${process.env.REACT_APP_API_URL}` +
      `/vendor-client-brand-matrix?` +
      params.toString()

    if (eventSourceRef.current) {
      eventSourceRef.current.close()
    }

    const es =
      new EventSource(url)

    eventSourceRef.current = es

    es.addEventListener(
      'progress',
      event => {
        const parsed =
          JSON.parse(
            (event as MessageEvent).data
          ) as VendorPayload & {
            count: number
            totalVendors: number
          }
        console.log('GOAL2_PROGRESS', parsed)

        setProgress({
          count:
            parsed.count ?? 0,

          total:
            parsed.totalVendors ?? 0
        })

        if (parsed.brands?.length) {
          setBrands(
            parsed.brands
          )
        }

        if (parsed.brandNames) {
          setBrandNames(parsed.brandNames)
        }

        if (
          parsed.sourceFrom ||
          parsed.sourceTo ||
          parsed.historicalMonths
        ) {
          setSourcePeriod({
            sourceFrom:
              parsed.sourceFrom ?? null,

            sourceTo:
              parsed.sourceTo ?? null,

            historicalMonths:
              parsed.historicalMonths ?? null
          })
        }

        setData(prev => {
          const idx =
            prev.findIndex(
              vendor =>
                vendor.codeVendor ===
                parsed.codeVendor
            )

          if (idx >= 0) {
            const copy =
              prev.slice()

            copy[idx] =
              parsed

            return copy
          }

          return [
            ...prev,
            parsed
          ]
        })
      }
    )

    es.addEventListener(
      'done',
      event => {
        const parsed =
          JSON.parse(
            (event as MessageEvent).data
          )

        setProgress({
          count:
            parsed.count ?? 0,

          total:
            parsed.totalVendors ?? 0
        })

        setDownloadUrl(
          parsed.downloadUrl ?? null
        )

        setPeople(parsed.people ?? [])

        if (
          parsed.sourceFrom ||
          parsed.sourceTo ||
          parsed.historicalMonths
        ) {
          setSourcePeriod({
            sourceFrom:
              parsed.sourceFrom ?? null,

            sourceTo:
              parsed.sourceTo ?? null,

            historicalMonths:
              parsed.historicalMonths ?? null
          })
        }

        setIsFetching(false)

        es.close()
        eventSourceRef.current = null
      }
    )

    es.addEventListener(
      'failed',
      event => {
        const parsed =
          JSON.parse(
            (event as MessageEvent).data
          )

        setError(
          parsed.error ??
          'No se pudo generar el reporte.'
        )

        setIsFetching(false)

        es.close()
        eventSourceRef.current = null
      }
    )

    es.onerror = error => {
      console.error(
        'SSE error',
        error
      )

      if (
        es.readyState ===
        EventSource.CLOSED
      ) {
        setError(current =>
          current ??
          'Se interrumpió la conexión con el servidor.'
        )

        setIsFetching(false)
        eventSourceRef.current = null
      }
    }
  }

  const stopStream = () => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close()
      eventSourceRef.current = null
    }
    setIsFetching(false)
  }

  const handleDownload = async () => {
    if (!downloadUrl) return
    const token = getToken()
    const res = await fetch(`${process.env.REACT_APP_API_URL}${downloadUrl}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
    const blob = await res.blob()
    const link = document.createElement('a')
    link.href = window.URL.createObjectURL(blob)
    const from = searchToFilters(history.location.search).from
    link.download = `objetivos_promedio_${moment(from).tz("America/Argentina/Buenos_Aires").format('YYYY-MM')}_industrias_saladillo.xlsx`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const handleSort = (column: OrderableColumn) => {
    if (orderBy === column) {
      setOrderDirection(orderDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setOrderBy(column)
      setOrderDirection('asc')
    }
  }

  const sortedClients = useMemo(() => {
    const clients = [...(selectedVendor?.clients ?? [])]

    return clients.sort((a, b) => {
      let result = 0

      if (orderBy.startsWith('brand-')) {
        const brandId = Number(orderBy.replace('brand-', ''))
        result = Number(a.totalsByBrand?.[brandId] ?? 0) - Number(b.totalsByBrand?.[brandId] ?? 0)
      } else {
        switch (orderBy) {
          case 'codigo_cliente':
            result = Number(a.codigo_cliente ?? 0) - Number(b.codigo_cliente ?? 0)
            break
          case 'nombre':
            result = String(a.nombre ?? '').localeCompare(String(b.nombre ?? ''))
            break
          case 'portfolioPercentage':
            result = Number(a.portfolioPercentage ?? 0) - Number(b.portfolioPercentage ?? 0)
            break
          case 'coverage':
            result =
              Number(Number(a.total ?? 0) > 0) -
              Number(Number(b.total ?? 0) > 0)
            break
          case 'participation':
            result = Number(Boolean(a.reached)) - Number(Boolean(b.reached))
            break
          case 'goal':
            result = Number(a.goal ?? 0) - Number(b.goal ?? 0)
            break
          case 'total':
            result = Number(a.total ?? 0) - Number(b.total ?? 0)
            break
          case 'scope':
            result = Number(a.scope ?? 0) - Number(b.scope ?? 0)
            break
          case 'deficit':
            result = Number(a.deficit ?? 0) - Number(b.deficit ?? 0)
            break
        }
      }

      return orderDirection === 'asc' ? result : -result
    })
  }, [selectedVendor, orderBy, orderDirection])

  const noWrapCell = {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: 270
  }

  const positiveValueCellStyle = (value: number) => ({
    fontWeight: 'bold',
    color: '#FFFFFF',
    backgroundColor:
      Number(value ?? 0) > 0
        ? '#008457'
        : '#d32f2f',
    whiteSpace: 'nowrap'
  })

  const reachedCellStyle = (reached: boolean) => ({
    fontWeight: 'bold',
    color: '#FFFFFF',
    backgroundColor:
      reached
        ? '#008457'
        : '#d32f2f',
    whiteSpace: 'nowrap'
  })

  const neutralMoneyCellStyle = {
    whiteSpace: 'nowrap'
  }

  const vendorGoalCellStyle = {
    fontWeight: 'bold',
    color: '#FFFFFF',
    backgroundColor: '#4F81BD',
    whiteSpace: 'nowrap'
  }

  const cardStyle = {
    p: 2,
    borderRadius: 2,
    cursor: 'pointer',
    transition: 'all .15s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: 4
    }
  }

  const statusTextStyle = (ok: boolean) => ({
    fontWeight: 'bold',
    color: ok ? '#008457' : '#d32f2f',
    whiteSpace: 'nowrap',
    textAlign: 'center'
  })

  const reachedColor = (reached: boolean) =>
    reached ? 'success.main' : 'warning.main'

  const achievementColor = (scope: number) => {
    if (Number(scope ?? 0) >= 100) return 'success.main'
    if (Number(scope ?? 0) > 0) return 'warning.main'
    return 'error.main'
  }

  const percentage = (value: number) =>
    `${Number(value ?? 0).toFixed(0)}%`

  const percentage2 = (value: number) =>
    `${Number(value ?? 0).toFixed(2)}%`

  const vendorTotalsByBrand = selectedVendor
    ? getVendorTotalsByBrand(selectedVendor)
    : {}

  return (

    <Stack sx={{ height: '100%' }}>
      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
        <FiltersBar defaultFilters={initDefaultFilters}>
          <FilterFromMonth name="from" label={t('month', { ns: 'goal' })} />
        </FiltersBar>
        <Button onClick={startStream} disabled={isFetching} size="large" style={{ textTransform: 'none' }}>
          {t('generate')}
        </Button>

        <Button onClick={stopStream} disabled={!isFetching} size="large" style={{ textTransform: 'none' }}>
          {t('cancel')}
        </Button>

        <Button variant="contained" onClick={handleDownload} disabled={!downloadUrl} size="large" style={{ textTransform: 'none' }}>
          {t('downloadReport')}&nbsp;&nbsp;<FiletypeXls size={25} />
        </Button>
      </Stack>

      <Paper sx={{ m: 1, p: 1, borderRadius: 2 }}>
        <Box display="flex" alignItems="center" gap={1}>
          {isFetching && <CircularProgress size={22} />}
          <Typography variant="body2" sx={{ minWidth: 80 }}>
            {progress.count}/{progress.total || 0}
          </Typography>
          <Box flexGrow={1}>
            <LinearProgress color='success' variant="determinate" value={progress.total ? (progress.count / progress.total) * 100 : 0} style={{ height: 10, borderRadius: 5 }} />
          </Box>
        </Box>
      </Paper>
      {error && (
        <Paper
          sx={{
            mx: 1,
            mb: 1,
            p: 1.5,
            borderRadius: 2
          }}
        >
          <Typography color="error">
            {error}
          </Typography>
        </Paper>
      )}
      {!isFetching && !selectedPerson && people.length > 0 && (
        <Box sx={{ m: 1 }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6">
              Objetivo 2 - Premios por vendedor
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Sujeto al alcance del presupuesto
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, minmax(320px, 1fr))',
              gap: 2,
              '@media (max-width: 1200px)': {
                gridTemplateColumns: 'repeat(2, minmax(320px, 1fr))'
              },
              '@media (max-width: 800px)': {
                gridTemplateColumns: '1fr'
              }
            }}
          >
            {people.map(person => (
              <Paper
                key={person.personId}
                sx={cardStyle}
                onClick={() => {
                  setSelectedPerson(person)
                  setSelectedVendor(null)
                }}
              >
                <Stack spacing={1.5}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" fontWeight="bold">
                      {person.personName}
                    </Typography>

                    <Typography
                      variant="h5"
                      fontWeight="bold"
                      color={person.prizeScope > 0 ? 'success.main' : 'text.secondary'}
                    >
                      {percentage(person.prizeScope)}
                    </Typography>
                  </Stack>

                  <Box sx={{
                    borderTop: '1px solid',
                    borderColor: darkModeEnabled
                      ? 'rgba(255,255,255,0.12)'
                      : 'rgba(0,0,0,0.12)', pt: 1
                  }}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2">
                        PREMIO POSIBLE
                      </Typography>

                      <Typography fontWeight="bold">
                        {formattedCurrency(person.possiblePrize, 0)}
                      </Typography>
                    </Stack>

                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2">
                        PREMIO ALCANZADO
                      </Typography>

                      <Typography fontWeight="bold">
                        {formattedCurrency(person.reachedPrize, 0)}
                      </Typography>
                    </Stack>
                  </Box>

                  <Box sx={{
                    borderTop: '1px solid',
                    borderColor: darkModeEnabled
                      ? 'rgba(255,255,255,0.12)'
                      : 'rgba(0,0,0,0.12)', pt: 1
                  }}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2">
                        COBERTURA
                      </Typography>

                      <Typography fontWeight="bold">
                        {person.coverageReachedClients}/{person.coverageBaseClients}
                        {' · '}
                        {percentage(person.coverageScope)}
                      </Typography>
                    </Stack>

                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2">
                        PARTICIPACIÓN
                      </Typography>

                      <Typography fontWeight="bold">
                        {person.participationReachedClients}/{person.participationBaseClients}
                        {' · '}
                        {percentage(person.participationClientsScope)}
                      </Typography>
                    </Stack>
                  </Box>
                </Stack>
              </Paper>
            ))}
          </Box>
        </Box>
      )}
      {!isFetching && selectedPerson && !selectedVendor && (
        <Box sx={{ m: 1 }}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              onClick={() => {
                setSelectedPerson(null)
                setSelectedVendor(null)
              }}
              style={{ textTransform: 'none' }}
            >
              Volver
            </Button>

            <Typography variant="h6">
              {selectedPerson.personName}
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Premio posible: {formattedCurrency(selectedPerson.possiblePrize, 0)}
            </Typography>
          </Stack>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(440px, 1fr))',
              gap: 2
            }}
          >
            {selectedPerson.vendors.map(vendor => (
              <Paper
                key={vendor.codeVendor}
                sx={cardStyle}
                onClick={() => setSelectedVendor(vendor)}
              >
                <Stack spacing={1.5}>

                  {/* FILA 1 */}
                  <Box
                    sx={{
                      textAlign: 'center',
                      display: 'flex',
                      alignItems: 'baseline',
                      justifyContent: 'center',
                      gap: 1
                    }}
                  >
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ fontWeight: 500 }}
                    >
                      ZONA ASIGNA
                    </Typography>

                    <Typography
                      variant="h5"
                      fontWeight="bold"
                    >
                      {percentage(vendor.assignmentPercentage)}
                    </Typography>
                  </Box>

                  {/* FILA 2 */}
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography fontWeight="bold">
                      {vendor.codeVendor} - {vendor.vendor}
                    </Typography>

                    <Box sx={{ textAlign: 'center', display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-evenly' }}>
                      <Typography variant="body2" color="text.secondary">
                        {vendor.codeZone} - {vendor.zone}
                      </Typography>

                      {!vendor.participatesObjective2 && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 0.5 }}
                        >
                          No participa del Objetivo 2
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  {/* FILAS 3 A 6 */}
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '135px minmax(130px, 1fr) minmax(155px, 1fr)',
                      columnGap: 1.5,
                      rowGap: 0.75,
                      alignItems: 'center',
                      borderTop: '1px solid',
                      borderColor: darkModeEnabled
                        ? 'rgba(255,255,255,0.12)'
                        : 'rgba(0,0,0,0.12)',
                      pt: 1.5,
                      '& > *': {
                        whiteSpace: 'nowrap'
                      }
                    }}
                  >
                    {/* FILA 3 */}
                    <Box />
                    <Typography variant="subtitle2" fontWeight="bold" textAlign="center" sx={{ whiteSpace: 'nowrap' }}                    >
                      COBERTURA ({percentage(vendor.coverageWeight)})
                    </Typography>

                    <Typography variant="subtitle2" fontWeight="bold" textAlign="center" sx={{ whiteSpace: 'nowrap' }}                    >
                      PARTICIPACIÓN ({percentage(vendor.participationWeight)})
                    </Typography>

                    {/* FILA 4 */}
                    <Typography variant="body2">
                      Cumplido
                    </Typography>

                    <Typography variant="body2" textAlign="right"
                      color={achievementColor(vendor.coverageScope)}

                    //color={reachedColor(vendor.coverageBaseClients > 0 &&  vendor.coverageReachedClients >= vendor.coverageBaseClients)}
                    >
                      {vendor.coverageReachedClients}/{vendor.coverageBaseClients}
                      {' · '}
                      {percentage2(vendor.coverageScope)}
                    </Typography>

                    <Typography variant="body2" textAlign="right"
                      //color={reachedColor(vendor.participationReachedClients > 0)}
                      color={achievementColor(vendor.participationScope)}
                    >
                      {vendor.participationReachedClients}/{vendor.coverageBaseClients}
                      {' · '}
                      {percentage2(vendor.participationScope)}
                    </Typography>

                    {/* FILA 5 */}
                    <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                      Premio posible
                    </Typography>

                    <Typography variant="body2" textAlign="right">
                      {formattedCurrency(vendor.coveragePossiblePrize, 0)}
                    </Typography>

                    <Typography variant="body2" textAlign="right">
                      {formattedCurrency(vendor.participationPossiblePrize, 0)}
                    </Typography>

                    {/* FILA 6 */}
                    <Typography variant="body2" fontWeight="bold">
                      Premio alcanzado
                    </Typography>

                    <Typography variant="body2" fontWeight="bold" textAlign="right">
                      {formattedCurrency(vendor.coverageReachedPrize, 0)}
                    </Typography>

                    <Typography variant="body2" fontWeight="bold" textAlign="right">
                      {formattedCurrency(vendor.participationReachedPrize, 0)}
                    </Typography>
                  </Box>

                  {/* FILA 7 */}
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{
                      borderTop: '1px solid',
                      borderColor: darkModeEnabled
                        ? 'rgba(255,255,255,0.12)'
                        : 'rgba(0,0,0,0.12)',
                      pt: 1
                    }}
                  >
                    <Typography fontWeight="bold">
                      TOTAL ALCANZADO
                    </Typography>

                    <Typography fontWeight="bold">
                      {formattedCurrency(vendor.reachedPrize, 0)}
                    </Typography>
                  </Stack>

                </Stack>
              </Paper>
            ))}
          </Box>
        </Box>
      )}
      {selectedPerson && selectedVendor && (
        <>


          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ mx: 1, mt: 1 }}
          >
            <Button
              variant="outlined"
              onClick={() => setSelectedVendor(null)}
              style={{ textTransform: 'none' }}
            >
              Volver a zonas
            </Button>

            <Box>
              <Typography variant="h6">
                {selectedPerson.personName}
              </Typography>

              <Typography variant="body2" color="text.secondary">
                {selectedVendor.codeVendor} - {selectedVendor.vendor}
                {' · '}
                {selectedVendor.codeZone} - {selectedVendor.zone}
              </Typography>
            </Box>
          </Stack>
          <Stack style={{ display: 'flex', flexDirection: 'column', height: '75%', borderRadius: "10px", margin: "10px", minWidth: "500px" }}>
            <ISTableTitle title={t('title')} />

            <TableContainer sx={{ overflowX: 'auto', height: '100%' }}>
              <Table stickyHeader size="medium">
                <TableHead>
                  <TableRow selected>

                    <ISTableHeaderCell
                      align="right"
                      sortDirection={orderBy === 'codigo_cliente' ? orderDirection : false}
                      sx={{ position: 'sticky', top: 0, zIndex: 10 }}
                    >
                      <TableSortLabel
                        active={orderBy === 'codigo_cliente'}
                        direction={orderBy === 'codigo_cliente' ? orderDirection : 'asc'}
                        onClick={() => handleSort('codigo_cliente')}
                      >
                        CLIENTE
                      </TableSortLabel>
                    </ISTableHeaderCell>

                    <ISTableHeaderCell
                      sortDirection={orderBy === 'nombre' ? orderDirection : false}
                      sx={{ position: 'sticky', top: 0, zIndex: 10 }}
                    >
                      <TableSortLabel
                        active={orderBy === 'nombre'}
                        direction={orderBy === 'nombre' ? orderDirection : 'asc'}
                        onClick={() => handleSort('nombre')}
                      >
                        NOMBRE
                      </TableSortLabel>
                    </ISTableHeaderCell>
                    <ISTableHeaderCell
                      align="right"
                      sx={{ minWidth: 130 }}
                      sortDirection={orderBy === 'portfolioPercentage' ? orderDirection : false}
                    >
                      <TableSortLabel
                        active={orderBy === 'portfolioPercentage'}
                        direction={orderBy === 'portfolioPercentage' ? orderDirection : 'asc'}
                        onClick={() => handleSort('portfolioPercentage')}
                      >
                        % CARTERA
                      </TableSortLabel>
                    </ISTableHeaderCell>

                    <ISTableHeaderCell
                      align="center"
                      sx={{ minWidth: 140 }}
                      sortDirection={orderBy === 'coverage' ? orderDirection : false}
                    >
                      <TableSortLabel
                        active={orderBy === 'coverage'}
                        direction={orderBy === 'coverage' ? orderDirection : 'asc'}
                        onClick={() => handleSort('coverage')}
                      >
                        COBERTURA ({percentage(selectedVendor.coverageWeight)})
                      </TableSortLabel>
                    </ISTableHeaderCell>

                    <ISTableHeaderCell
                      align="center"
                      sx={{ minWidth: 160 }}
                      sortDirection={orderBy === 'participation' ? orderDirection : false}
                    >
                      <TableSortLabel
                        active={orderBy === 'participation'}
                        direction={orderBy === 'participation' ? orderDirection : 'asc'}
                        onClick={() => handleSort('participation')}
                      >
                        PARTICIPACIÓN ({percentage(selectedVendor.participationWeight)})
                      </TableSortLabel>
                    </ISTableHeaderCell>

                    <ISTableHeaderCell
                      align="right"
                      sx={{ minWidth: 200 }}
                      sortDirection={orderBy === 'goal' ? orderDirection : false}
                    >
                      <TableSortLabel
                        active={orderBy === 'goal'}
                        direction={orderBy === 'goal' ? orderDirection : 'asc'}
                        onClick={() => handleSort('goal')}
                      >
                        OBJETIVO
                      </TableSortLabel>
                    </ISTableHeaderCell>

                    <ISTableHeaderCell
                      align="right"
                      sortDirection={orderBy === 'total' ? orderDirection : false}
                      sx={{ position: 'sticky', top: 0, zIndex: 10 }}
                    >
                      <TableSortLabel
                        active={orderBy === 'total'}
                        direction={orderBy === 'total' ? orderDirection : 'asc'}
                        onClick={() => handleSort('total')}
                      >
                        PEDIDOS
                      </TableSortLabel>
                    </ISTableHeaderCell>

                    <ISTableHeaderCell
                      align="right"
                      sortDirection={orderBy === 'scope' ? orderDirection : false}
                    >
                      <TableSortLabel
                        active={orderBy === 'scope'}
                        direction={orderBy === 'scope' ? orderDirection : 'asc'}
                        onClick={() => handleSort('scope')}
                      >
                        ALCANCE
                      </TableSortLabel>
                    </ISTableHeaderCell>

                    <ISTableHeaderCell
                      align="right"
                      sortDirection={orderBy === 'deficit' ? orderDirection : false}
                    >
                      <TableSortLabel
                        active={orderBy === 'deficit'}
                        direction={orderBy === 'deficit' ? orderDirection : 'asc'}
                        onClick={() => handleSort('deficit')}
                      >
                        DÉFICIT
                      </TableSortLabel>
                    </ISTableHeaderCell>

                    {brands.map(b => {
                      const column = `brand-${b}` as OrderableColumn

                      return (
                        <ISTableHeaderCell
                          key={b}
                          align="right"
                          sortDirection={orderBy === column ? orderDirection : false}
                          sx={{ minWidth: 130, whiteSpace: 'nowrap' }}
                        >
                          <TableSortLabel
                            active={orderBy === column}
                            direction={orderBy === column ? orderDirection : 'asc'}
                            onClick={() => handleSort(column)}
                          >
                            {brandNames[b] ?? `M${b}`}
                          </TableSortLabel>
                        </ISTableHeaderCell>
                      )
                    })}

                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedClients.map(c => {
                    const rowTotal = Number(c.total ?? 0)
                    const rowGoal = Number(c.goal ?? 0)
                    const reached = typeof c.reached === 'boolean' ? c.reached : rowGoal > 0 && rowTotal > rowGoal
                    const covered = rowTotal > 0

                    return (
                      <ISTableRow key={`${selectedVendor.codeVendor}-${c.codigo_cliente}`} color="default">
                        <TableCell align="right">{c.codigo_cliente}</TableCell>

                        <TableCell sx={noWrapCell}>
                          <Tooltip placement="top" title={c.nombre}>
                            <span>{c.nombre}</span>
                          </Tooltip>
                        </TableCell>

                        <TableCell align="right" sx={reachedCellStyle(reached)}>
                          {Number(c.portfolioPercentage ?? 0).toFixed(2)}%
                        </TableCell>

                        <TableCell align="center">
                          <Typography sx={statusTextStyle(covered)}>
                            {covered ? '✔' : '✖'}
                          </Typography>
                        </TableCell>

                        <TableCell align="center">
                          <Typography sx={statusTextStyle(reached)}>
                            {reached ? '✔' : '✖'}
                          </Typography>
                        </TableCell>

                        <TableCell align="right" sx={neutralMoneyCellStyle}>
                          {formattedCurrency(rowGoal)}
                        </TableCell>

                        <TableCell align="right" sx={reachedCellStyle(reached)}>
                          {formattedCurrency(rowTotal)}
                        </TableCell>

                        <TableCell align="right" sx={reachedCellStyle(reached)}>
                          {Number(c.scope ?? 0).toFixed(2)}%
                        </TableCell>

                        <TableCell align="right" sx={reachedCellStyle(reached)}>
                          {formattedCurrency(Number(c.deficit ?? 0))}
                        </TableCell>

                        {brands.map(b => {
                          const value = Number(c.totalsByBrand?.[b] ?? 0)

                          return (
                            <TableCell key={`${c.codigo_cliente}-${b}`} align="right" sx={positiveValueCellStyle(value)}>
                              {formattedCurrency(value)}
                            </TableCell>
                          )
                        })}
                      </ISTableRow>
                    )
                  })}

                  <ISTableRow color="default">
                    <TableCell sx={vendorGoalCellStyle} />

                    <TableCell sx={{ ...vendorGoalCellStyle, ...noWrapCell }}>
                      {t('totalVendor', { ns: 'goal' })}
                    </TableCell>

                    <TableCell sx={vendorGoalCellStyle} />

                    <TableCell align="center" sx={vendorGoalCellStyle}>
                      {selectedVendor.coverageReachedClients}/{selectedVendor.coverageBaseClients}
                      {' · '}
                      {percentage2(selectedVendor.coverageScope)}
                    </TableCell>

                    <TableCell align="center" sx={vendorGoalCellStyle}>
                      {selectedVendor.participationReachedClients}/{selectedVendor.coverageBaseClients}
                      {' · '}
                      {percentage2(selectedVendor.participationScope)}
                    </TableCell>

                    {/* OBJETIVO */}
                    <TableCell sx={vendorGoalCellStyle} />

                    {/* PEDIDOS */}
                    <TableCell sx={vendorGoalCellStyle} />

                    {/* ALCANCE */}
                    <TableCell sx={vendorGoalCellStyle} />

                    {/* DÉFICIT */}
                    <TableCell sx={vendorGoalCellStyle} />

                    {brands.map(b => {
                      const value = Number(vendorTotalsByBrand[b] ?? 0)

                      return (
                        <TableCell key={`total-vendor-${selectedVendor.codeVendor}-${b}`} align="right" sx={positiveValueCellStyle(value)}>
                          {formattedCurrency(value)}
                        </TableCell>
                      )
                    })}
                  </ISTableRow>

                  <ISTableRow color="default">
                    <TableCell sx={vendorGoalCellStyle} />

                    <TableCell sx={{ ...vendorGoalCellStyle, ...noWrapCell }}>
                      Resultado para el objetivo
                    </TableCell>

                    {/* % CARTERA */}
                    <TableCell sx={vendorGoalCellStyle} />

                    {/* COBERTURA */}
                    <TableCell
                      align="center"
                      sx={vendorGoalCellStyle}
                    >
                      {percentage2(selectedVendor.coverageContribution)}
                    </TableCell>

                    {/* PARTICIPACIÓN */}
                    <TableCell
                      align="center"
                      sx={vendorGoalCellStyle}
                    >
                      {percentage2(selectedVendor.participationContribution)}
                    </TableCell>

                    {/* OBJETIVO */}
                    <TableCell sx={vendorGoalCellStyle} />

                    {/* PEDIDOS */}
                    <TableCell sx={vendorGoalCellStyle} />

                    {/* ALCANCE TOTAL OBJETIVO 2 */}
                    <TableCell
                      align="right"
                      sx={vendorGoalCellStyle}
                    // sx={positiveValueCellStyle(Number(selectedVendor.goal2Scope ?? 0))}                                        
                    >
                      {percentage2(selectedVendor.goal2Scope)}
                    </TableCell>

                    {/* DÉFICIT */}
                    <TableCell sx={vendorGoalCellStyle} />

                    {brands.map(b => (
                      <TableCell key={`goal2-${selectedVendor.codeVendor}-${b}`} sx={vendorGoalCellStyle} />
                    ))}
                  </ISTableRow>

                  {/* ---- CLIENTES POR MARCA (MES) ---- */}
                  <ISTableRow color="default">
                    <TableCell sx={vendorGoalCellStyle} />

                    <TableCell
                      sx={vendorGoalCellStyle}
                    >
                      {t('clientsByBrandCurrent', {
                        ns: 'goal'
                      })}
                    </TableCell>

                    {/* OBJETIVO */}
                    <TableCell sx={vendorGoalCellStyle} />

                    {/* % CARTERA */}
                    <TableCell sx={vendorGoalCellStyle} />
                    <TableCell sx={vendorGoalCellStyle} />
                    <TableCell sx={vendorGoalCellStyle} />
                    {/* TOTAL DE CLIENTES */}
                    <TableCell
                      align="right"
                      sx={positiveValueCellStyle(
                        Number(
                          selectedVendor.totalClientsCurrent ?? 0
                        )
                      )}
                    >
                      {selectedVendor.totalClientsCurrent ?? 0}
                    </TableCell>

                    {/* ALCANCE */}
                    <TableCell sx={vendorGoalCellStyle} />

                    {/* DÉFICIT */}
                    <TableCell sx={vendorGoalCellStyle} />

                    {/* CLIENTES POR MARCA */}
                    {brands.map(b => {
                      const value =
                        Number(
                          selectedVendor.brandClientCountCurrent?.[b] ?? 0
                        )

                      return (
                        <TableCell
                          key={`cbc-${selectedVendor.codeVendor}-${b}`}
                          align="right"
                          sx={positiveValueCellStyle(value)}
                        >
                          {value}
                        </TableCell>
                      )
                    })}
                  </ISTableRow>

                  {/* ---- CLIENTES POR MARCA (PERIODO) ---- */}
                  <ISTableRow color="default">
                    <TableCell sx={vendorGoalCellStyle} />

                    <TableCell sx={vendorGoalCellStyle}>
                      {sourcePeriod.sourceFrom &&
                        sourcePeriod.sourceTo
                        ? (
                          `Clientes por Marca (` +
                          `${moment(
                            sourcePeriod.sourceFrom
                          ).format('MM/YYYY')} - ` +
                          `${moment(
                            sourcePeriod.sourceTo
                          ).format('MM/YYYY')})`
                        )
                        : 'Clientes (Período Histórico)'}
                    </TableCell>

                    {/* OBJETIVO */}
                    <TableCell sx={vendorGoalCellStyle} />

                    {/* % CARTERA */}
                    <TableCell sx={vendorGoalCellStyle} />
                    <TableCell sx={vendorGoalCellStyle} />
                    <TableCell sx={vendorGoalCellStyle} />

                    {/* TOTAL DE CLIENTES DEL PERÍODO */}
                    <TableCell
                      align="right"
                      sx={positiveValueCellStyle(
                        Number(
                          selectedVendor.totalClientsPeriod ?? 0
                        )
                      )}
                    >
                      {selectedVendor.totalClientsPeriod ?? 0}
                    </TableCell>

                    {/* ALCANCE */}
                    <TableCell sx={vendorGoalCellStyle} />

                    {/* DÉFIT */}
                    <TableCell sx={vendorGoalCellStyle} />

                    {/* CLIENTES POR MARCA DEL PERÍODO */}
                    {brands.map(b => {
                      const value =
                        Number(
                          selectedVendor.brandClientCountPeriod?.[b] ?? 0
                        )

                      return (
                        <TableCell
                          key={`cbp-${selectedVendor.codeVendor}-${b}`}
                          align="right"
                          sx={positiveValueCellStyle(value)}
                        >
                          {value}
                        </TableCell>
                      )
                    })}
                  </ISTableRow>
                </TableBody>
              </Table>
            </TableContainer>
            {isFetching &&
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '10px' }}>
                <CircularProgress size={25} />
              </div>
            }
          </Stack>
        </>
      )
      }
    </Stack >
  )
}

const mapStateToProps = (state: AppState) => ({ session: state.session })
export default connect(mapStateToProps)(VendorClientBrandMatrixSSE)
